import { Box, Coffee, Soup, Utensils, type LucideIcon } from 'lucide-react';

/**
 * Shared constants & style helpers for the redesigned Container Types screen
 * (Direction 1a: data table + edit sheet).
 *
 * The Vytal Admin theme does not expose the `--brand-green` / `--status-warning`
 * tokens the design handoff references; it uses `--color-success-main`,
 * `--color-warning-main` and `--destructive` instead. The soft-badge tints below
 * are built with `color-mix()` against `transparent` so they read correctly in
 * both light and dark mode.
 */

// --- Segment → category icon (fallback when a container has no photo) --------
// Keyed on the container's segment (see SEGMENT_VALUES), not its group.
const SEGMENT_ICON_BY_NAME: Record<string, LucideIcon> = {
  Drinks: Coffee,
  Food: Soup,
  Cutlery: Utensils,
};

export function getSegmentIcon(segment?: string | null): LucideIcon {
  return (segment && SEGMENT_ICON_BY_NAME[segment]) || Box;
}

// --- Class pill colours -------------------------------------------------------
export type PillStyle = {
  backgroundColor: string;
  color: string;
  border: string;
};

const CLASS_PILL_GREEN: PillStyle = {
  backgroundColor:
    'color-mix(in oklab, var(--color-success-main) 15%, transparent)',
  color: '#0f7a52',
  border:
    '1px solid color-mix(in oklab, var(--color-success-main) 35%, transparent)',
};

const CLASS_PILL_AMBER: PillStyle = {
  backgroundColor:
    'color-mix(in oklab, var(--color-warning-main) 24%, transparent)',
  color: '#b45309',
  border:
    '1px solid color-mix(in oklab, var(--color-warning-main) 45%, transparent)',
};

const CLASS_PILL_NEUTRAL: PillStyle = {
  backgroundColor: 'var(--muted)',
  color: 'var(--muted-foreground)',
  border: '1px solid var(--border)',
};

/**
 * Product portfolio → green, Phase-out → amber, Secondary portfolio → neutral.
 * Matched loosely on the class name so backend label tweaks keep working.
 */
export function getClassPillStyle(className?: string | null): PillStyle {
  const c = (className || '').toLowerCase();
  if (c.includes('product')) return CLASS_PILL_GREEN;
  if (c.includes('phase')) return CLASS_PILL_AMBER;
  return CLASS_PILL_NEUTRAL;
}

// --- "Edited" pill (amber) ----------------------------------------------------
export const EDITED_PILL_STYLE: PillStyle = CLASS_PILL_AMBER;

// --- Segment dot colours ------------------------------------------------------
export function getSegmentDotColor(segment?: string | null): string {
  switch (segment) {
    case 'Food':
      return 'var(--color-success-main)';
    case 'Drinks':
      return 'oklch(0.62 0.13 233)'; // info blue
    case 'Cutlery':
    default:
      return 'var(--muted-foreground)';
  }
}

// --- Warning accent (dirty row border, save-bar dot) --------------------------
export const WARNING_ACCENT = 'var(--color-warning-main)';

// --- Optional (toggleable) columns -------------------------------------------
export type OptionalColumn = {
  id: string;
  /** i18n key under the `EditContainerInfo` namespace. */
  labelKey: string;
  /** Whether this column is also a faceted filter. */
  filter?: boolean;
  align?: 'left' | 'center' | 'right';
};

export const OPTIONAL_COLUMNS: OptionalColumn[] = [
  { id: 'id', labelKey: 'colId' },
  { id: 'class', labelKey: 'colClass', filter: true },
  { id: 'group', labelKey: 'colGroup', filter: true },
  { id: 'segment', labelKey: 'segment', filter: true },
  { id: 'sizeHelper', labelKey: 'colSize', align: 'center' },
  { id: 'sku', labelKey: 'colSku' },
  { id: 'countries', labelKey: 'colCountries', filter: true },
  { id: 'weight', labelKey: 'colWeight', align: 'right' },
  { id: 'packingUnit', labelKey: 'colPack', align: 'right' },
  { id: 'branding', labelKey: 'colBranding' },
  { id: 'legalOwner', labelKey: 'colLegalOwner' },
  { id: 'color', labelKey: 'colColor' },
  { id: 'orderKey', labelKey: 'colOrder', align: 'right' },
  { id: 'archived', labelKey: 'colArchived', align: 'center' },
];

/** Sensible default: hide the noisier columns, keep the identity-adjacent ones. */
export const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  id: false,
  class: true,
  group: true,
  segment: true,
  sizeHelper: true,
  sku: true,
  countries: true,
  weight: true,
  packingUnit: false,
  branding: false,
  legalOwner: false,
  color: false,
  orderKey: false,
  archived: false,
};

export const SEGMENT_VALUES = ['Drinks', 'Food', 'Cutlery'] as const;

export const PAGE_SIZE = 10;

// --- Pricing (absorbed EditContainerPrices) -----------------------------------

export type SystemType = 'OpenSystem' | 'Event';

/**
 * How a raw price value is stored:
 * - `unitFloat` — decimal currency units (only `TransactionPrice`)
 * - `cents` — integer cents
 */
export type PriceScale = 'unitFloat' | 'cents';

/** Where a price value lives: the store-defaults config or the overdue table. */
export type PriceSource = 'store' | 'overdue';

export type PriceFieldDef = {
  /** Key in the store-defaults row / overdue price object. */
  key: string;
  /** i18n key under the `EditContainerInfo` namespace. */
  labelKey: string;
  scale: PriceScale;
  source: PriceSource;
  /** Matching key on `NewContainerPrice` (only open-system fields have one). */
  draftKey?: string;
};

export const OPEN_SYSTEM_PRICE_FIELDS: PriceFieldDef[] = [
  {
    key: 'TransactionPrice',
    labelKey: 'priceTransaction',
    scale: 'unitFloat',
    source: 'store',
    draftKey: 'transactionPrice',
  },
  {
    key: 'priceInclVatInCents',
    labelKey: 'priceOverdue',
    scale: 'cents',
    source: 'overdue',
    draftKey: 'overduePrice',
  },
  {
    key: 'creditsOnReturnInCents',
    labelKey: 'priceCredits',
    scale: 'cents',
    source: 'overdue',
    draftKey: 'creditsOnOverdueReturn',
  },
  {
    key: 'StoreLostCost',
    labelKey: 'priceStoreLostFull',
    scale: 'cents',
    source: 'store',
    draftKey: 'storeLostCost',
  },
  {
    key: 'StoreLostCostUpper',
    labelKey: 'priceStoreLostUpper',
    scale: 'cents',
    source: 'store',
    draftKey: 'storeLostCostUpper',
  },
  {
    key: 'StoreLostCostLower',
    labelKey: 'priceStoreLostLower',
    scale: 'cents',
    source: 'store',
    draftKey: 'storeLostCostLower',
  },
];

export const EVENT_PRICE_FIELDS: PriceFieldDef[] = [
  {
    key: 'RentalPrice',
    labelKey: 'priceRental',
    scale: 'cents',
    source: 'store',
  },
  {
    key: 'CleaningPrice',
    labelKey: 'priceCleaning',
    scale: 'cents',
    source: 'store',
  },
  { key: 'LostPrice', labelKey: 'priceLost', scale: 'cents', source: 'store' },
  {
    key: 'DepositPrice',
    labelKey: 'priceDeposit',
    scale: 'cents',
    source: 'store',
  },
  { key: 'CostPrice', labelKey: 'priceCost', scale: 'cents', source: 'store' },
  {
    key: 'LogisticPartnerCleaningPrice',
    labelKey: 'priceLogCleaning',
    scale: 'cents',
    source: 'store',
  },
];

export const PRICE_FIELDS_BY_SYSTEM: Record<SystemType, PriceFieldDef[]> = {
  OpenSystem: OPEN_SYSTEM_PRICE_FIELDS,
  Event: EVENT_PRICE_FIELDS,
};

/** EUR reference columns shown in the table's pricing mode. */
export const PRICING_REF_COLUMNS: Record<
  SystemType,
  { def: PriceFieldDef; labelKey: string }[]
> = {
  OpenSystem: [
    { def: OPEN_SYSTEM_PRICE_FIELDS[0], labelKey: 'refTransaction' },
    { def: OPEN_SYSTEM_PRICE_FIELDS[1], labelKey: 'refOverdue' },
    { def: OPEN_SYSTEM_PRICE_FIELDS[3], labelKey: 'refStoreLost' },
  ],
  Event: [
    { def: EVENT_PRICE_FIELDS[0], labelKey: 'refRental' },
    { def: EVENT_PRICE_FIELDS[3], labelKey: 'refDeposit' },
    { def: EVENT_PRICE_FIELDS[2], labelKey: 'refLost' },
  ],
};

const CURRENCY_SYMBOL_CACHE: Record<string, string> = {};

/** Narrow currency symbol ("€", "kr", "zł") with the code as fallback. */
export function getCurrencySymbol(currency: string): string {
  if (CURRENCY_SYMBOL_CACHE[currency] == null) {
    try {
      const part = new Intl.NumberFormat('en', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      })
        .formatToParts(0)
        .find((p) => p.type === 'currency');
      CURRENCY_SYMBOL_CACHE[currency] = part?.value ?? currency;
    } catch {
      CURRENCY_SYMBOL_CACHE[currency] = currency;
    }
  }
  return CURRENCY_SYMBOL_CACHE[currency];
}
